import { Button } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BankLogo } from 'src/Components/Shared/BankLogo';
import useFetchBankAccounts from 'src/Hooks/Queries/useFetchBankAccounts';
import { useCanCreate } from 'src/Hooks/Shared/useHasPermission';

import { DashboardCard } from './DashboardCard';
import styles from './HouseAccountCard.module.scss';
import { Props } from './HouseAccountCard.types';

const formatEuros = (amount: number): string =>
  amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const HouseAccountCard = ({ onShowBankAccounts }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: bankAccounts, isLoading, isError, refetch } = useFetchBankAccounts();
  const { hasPermission: canCreateBankAccount } = useCanCreate('bankAccount');

  const accounts = bankAccounts ?? [];
  const isEmpty = accounts.length === 0;
  const account = accounts[0];

  // Several accounts: the key figure is their sum, the subline says how many.
  const totalBalance = accounts.reduce(
    (total, current) => total + (current.latestBalance?.amount ?? 0),
    0
  );

  const latestStatement = accounts.reduce<Dayjs | undefined>((latest, current) => {
    const recordedAt = current.latestBalance?.recordedAt;
    if (!recordedAt) return latest;
    return !latest || dayjs(recordedAt).isAfter(latest) ? dayjs(recordedAt) : latest;
  }, undefined);

  const statementText = latestStatement
    ? t('expenses.dashboard.statementOf', 'relevé du {{date}}', {
        date: latestStatement.format('DD/MM/YYYY'),
      })
    : '';

  const accountLabel =
    accounts.length > 1
      ? t('expenses.dashboard.several', '{{count}} comptes', { count: accounts.length })
      : (account?.name ?? '');

  const sublineText = [accountLabel, statementText].filter(Boolean).join(', ');

  const handleAddAccount = () => navigate('new-bank-account');

  if (isEmpty && !isLoading && !isError) {
    return (
      <DashboardCard
        label={t('expenses.dashboard.houseAccount', 'Le compte de la maison')}
        figure={
          <span className={styles.empty}>
            {t('expenses.dashboard.noBankAccount', 'Aucun compte enregistré')}
          </span>
        }
        subline={
          <span>
            {t(
              'expenses.dashboard.addAccountHint',
              'Ajoutez le compte de la maison pour suivre son solde ici.'
            )}
          </span>
        }
        actions={
          // No permission means no button at all: never a silent disabled CTA.
          canCreateBankAccount ? (
            <Button size="sm" leftSection={<IconPlus size={18} />} onClick={handleAddAccount}>
              {t('bankAccounts.add', 'Ajouter un compte bancaire')}
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <DashboardCard
      label={t('expenses.dashboard.houseAccount', 'Le compte de la maison')}
      figure={
        <span className={totalBalance < 0 ? styles.negative : undefined}>
          {formatEuros(totalBalance)}
        </span>
      }
      subline={
        <>
          {accounts.length === 1 && account && (
            <BankLogo domain={account.bankDomain} name={account.bankName} size={22} />
          )}
          <span>{sublineText}</span>
        </>
      }
      isLoading={isLoading}
      isError={isError}
      onRetry={() => {
        void refetch();
      }}
      actions={
        <Button size="sm" variant="outline" onClick={onShowBankAccounts}>
          {t('expenses.dashboard.viewAccount', 'Voir le compte')}
        </Button>
      }
    />
  );
};

export default HouseAccountCard;
